/**
 * Sheet „Passwort setzen" (`access-person-password-sheet`, Spec 25,
 * Oberfläche D) — öffnet sich über der `AccessPersonSheet` aus der Zeile
 * „Passwort setzen …". Ein Feld, kein Wiederholfeld (D1: Der Administrator
 * liest, was er tippt — ein Wiederholfeld hilft ihm nicht).
 *
 * Eigenes, kleines Sheet statt eines dritten Feldes in `AccessPersonSheet`:
 * Das Passwort ist kein Stammdatum (`POST …/password` schreibt keine
 * Datenbankspalte, Fachregel 2) und hat deshalb keinen Platz im
 * „Speichern"-Rumpf des Stammdatenformulars — dieselbe Trennung wie zwischen
 * `AccessPersonSheet` und dem Sperren-Knopf.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { useSetPassword } from '@/features/access/api'
import { PasswordRevealField } from '@/features/access/PasswordRevealField'

interface AccessPersonPasswordSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly userId: string
  readonly onSaved: () => void
}

export function AccessPersonPasswordSheet({ open, onOpenChange, userId, onSaved }: AccessPersonPasswordSheetProps) {
  const setPasswordMutation = useSetPassword()
  const [password, setPassword] = useState('')
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const passwordRef = useRef<HTMLInputElement>(null)

  // Jedes Öffnen beginnt frisch — dieselbe Technik wie `AccessInviteSheet`.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setPassword('')
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  async function submit() {
    setFieldError(undefined)
    setGeneralError(undefined)
    try {
      await setPasswordMutation.mutateAsync({ userId, password })
      onSaved()
      onOpenChange(false)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          passwordRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[420px] md:data-[side=right]:!max-w-[420px]"
      >
        <form
          data-testid="access-person-password-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Passwort setzen
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <PasswordRevealField
              ref={passwordRef}
              label="Neues Passwort"
              testId="access-person-password-new"
              apiField="password"
              errorPrefix="access-person-password"
              required
              value={password}
              onChange={setPassword}
              error={fieldError}
            />
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="access-person-password-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="access-person-password-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="access-person-password-save"
                disabled={setPasswordMutation.isPending}
                aria-busy={setPasswordMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Setzen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
